import { memo, ReactNode, useMemo, useState } from 'react';
import {
  Importance,
  SIGNAL_KIND_TAGS,
  SignalKind
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Dropdown } from '@Components/Dropdown';
import { Placement, Size } from '@Components/designConstants';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { useShowWipActions } from '../activity-feed-utils';
import { SortBy } from '../activity-feed-constants';
import { ActivityFeedClientDropdown } from '../ActivityFeedClientDropdown';
import { useActivityFeedFilterContext } from '../useActivityFeedData';
import { EventTheme } from '../oracle-taxonomy-constants';
import { useOracleEventCounts } from '../useOracleEventCounts';
import {
  CHANNEL_OPTIONS,
  DEFAULT_FILTER_STATE,
  IMPORTANCE_OPTIONS,
  SIGNAL_OPTIONS,
  SORT_OPTIONS,
  THEME_OPTIONS
} from './activity-feed-filters-constants';
import {
  applyClientsDraftChange,
  clientsToCommitOnClose,
  countActiveFilters,
  makeArrayToggle
} from './activity-feed-filters-utils';
import { CheckFilterSection } from './CheckFilterSection';
import { DateFilterSection } from './DateFilterSection';
import { DropdownFilterSection } from './DropdownFilterSection';
import { FilterHeader } from './FilterHeader';
import { FilterSection } from './FilterSection';
import { AdvancedFiltersSection } from './AdvancedFiltersSection';
import { KindTogglesSection } from './KindTogglesSection';
import { StarredPinnedSection } from './StarredPinnedSection';

export const ActivityFeedFilters = memo((): ReactNode => {
  const { filterState, setFilterState, sortBy, setSortBy } = useActivityFeedFilterContext();
  const counts = useOracleEventCounts();
  const {
    clients,
    date,
    customStartDate,
    customEndDate,
    importance,
    signals,
    categories,
    channels
  } = filterState;
  const { cid } = useCompanyInfo();
  const showWip = useShowWipActions();

  // SignalTag → SignalKind aggregation; SignalTag.NONE falls through both risk and positive.
  const bySignal = useMemo<Record<SignalKind, number> | undefined>(() => {
    if (!counts) return undefined;
    return (Object.keys(SIGNAL_KIND_TAGS) as SignalKind[]).reduce<Record<SignalKind, number>>(
      (acc, signal) => {
        acc[signal] = SIGNAL_KIND_TAGS[signal].reduce(
          (sum, tag) => sum + (counts.bySignalTag[tag] ?? 0),
          0
        );
        return acc;
      },
      {} as Record<SignalKind, number>
    );
  }, [counts]);

  const toggleImportance = makeArrayToggle<Importance>(setFilterState, 'importance');
  const toggleSignal = makeArrayToggle<SignalKind>(setFilterState, 'signals');

  const handleClearFilters = () => {
    setFilterState(() => DEFAULT_FILTER_STATE);
  };

  return (
    <aside className='activity-feed-filters'>
      <FilterHeader filterCount={countActiveFilters(filterState)} onClear={handleClearFilters} />
      <FilterSection label='Sort by'>
        <Dropdown<SortBy>
          size={Size.LG}
          overlayPlacement={Placement.RIGHT}
          value={sortBy}
          options={SORT_OPTIONS}
          placeholder='Most recent'
          onClear={() => setSortBy(undefined)}
          onChange={setSortBy}
        />
      </FilterSection>
      {!cid && (
        <ClientsFilterSection
          // Remount on URL changes (e.g. "Clear filters") so the draft resets to the new value.
          key={clients.join(',')}
          selected={clients}
          onCommit={next => setFilterState(prev => ({ ...prev, clients: next }))}
        />
      )}
      {showWip && (
        <DropdownFilterSection
          label='Channels'
          options={CHANNEL_OPTIONS}
          selected={channels}
          onChange={next => setFilterState(prev => ({ ...prev, channels: next }))}
        />
      )}
      <DropdownFilterSection<EventTheme>
        label='Categories'
        options={THEME_OPTIONS}
        selected={categories}
        onChange={next => setFilterState(prev => ({ ...prev, categories: next }))}
      />
      <DateFilterSection
        date={date}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        onChange={next => setFilterState(prev => ({ ...prev, ...next }))}
      />
      {showWip && <KindTogglesSection />}
      {showWip && <StarredPinnedSection />}
      <CheckFilterSection<Importance>
        label='Importance'
        options={IMPORTANCE_OPTIONS}
        isChecked={val => importance.includes(val)}
        onToggle={toggleImportance}
        counts={counts?.byImportance}
      />
      <CheckFilterSection<SignalKind>
        label='Signals'
        options={SIGNAL_OPTIONS}
        isChecked={val => signals.includes(val)}
        onToggle={toggleSignal}
        counts={bySignal}
      />
      {showWip && <AdvancedFiltersSection />}
    </aside>
  );
});
ActivityFeedFilters.displayName = 'ActivityFeedFilters';

interface ClientsFilterSectionProps {
  selected: string[];
  onCommit: (next: string[]) => void;
}

// Edits stay in a draft and commit to the URL when the menu closes; clearing commits immediately.
const ClientsFilterSection = ({ selected, onCommit }: ClientsFilterSectionProps): ReactNode => {
  const [draft, setDraft] = useState(selected);

  const handleChange = (next: string[]): void => {
    const change = applyClientsDraftChange(next, selected);
    setDraft(change.draft);
    if (change.commit) {
      onCommit(change.commit);
    }
  };

  const handleMenuClose = (): void => {
    const commit = clientsToCommitOnClose(draft, selected);
    if (commit) {
      onCommit(commit);
    }
  };

  return (
    <FilterSection label='Clients'>
      <ActivityFeedClientDropdown
        value={draft}
        onChange={handleChange}
        onMenuClose={handleMenuClose}
        placeholder='All Clients'
        clearable
        // The filter panel scrolls on the y-axis, which forces overflow-x to clip too; portal the
        // menu out so a menu wider than the panel isn't cut off.
        menuPortalTarget={document.body}
      />
    </FilterSection>
  );
};
